import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MojoShareResourceComponent, MojoShareResourceConfig } from 'mojo-share-resource';
import { Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { getUsersLoggedIn } from '@collections/users';
import { MaterialAppearance } from '@material/material-appearance.enum';
import { TEAM_ROLES } from '@shared/models/team-role.enum';
import {
  SharedResources,
  SharedResourcesType,
} from '@core/shared-resources/shared-resources.model';
import {
  NotesTeamMembersModalComponent,
  NotesTeamMembersModalData,
} from '@modules/notes/components/modal/team-members/notes-team-members-modal.component';
import { getCurrentSharedResource } from '@core/shared-resources/store/shared-resources.selectors';

@Injectable({
  providedIn: 'root',
})
export class TeamMembersModalService implements OnDestroy {
  subscription = new Subscription();

  private readonly teamMembersText = 'Team members';

  private notesModalOptions = new Subject<string>();
  notesModalOptions$ = this.notesModalOptions.asObservable();

  constructor(private store: Store<{}>, private router: Router, private dialog: MatDialog) {
    this.listenToRouterEvents();
  }

  private listenToRouterEvents() {
    this.subscription.add(
      this.router.events
        .pipe(
          filter((event: Event) => event instanceof NavigationEnd),
          filter((event: NavigationEnd) => event.urlAfterRedirects.includes('(modal:team-members')),
          withLatestFrom(
            this.store.select(getUsersLoggedIn),
            this.store.select(getCurrentSharedResource),
          ),
          filter(
            ([, currentUser, currentSharedResource]) =>
              !!currentUser && !!currentSharedResource.resourceType,
          ),
          map(([_, currentUser, currentSharedResource]) => {
            if (SharedResourcesType.note !== currentSharedResource.resourceType) {
              return this.dialog.open<MojoShareResourceComponent, MojoShareResourceConfig>(
                MojoShareResourceComponent,
                {
                  data: this.setupDataByResource(currentUser.id, currentSharedResource),
                  panelClass: ['medium-size-modal', 'team-members'],
                },
              );
            }
          }),
          switchMap((teamDialog) => teamDialog.afterClosed()),
        )
        .subscribe((teamDialog) => {
          this.router.navigate([{ outlets: { modal: null } }]);
        }),
    );
  }

  public openNotesModal() {
    const matDialogRef = this.dialog.open<
      NotesTeamMembersModalComponent,
      NotesTeamMembersModalData
    >(NotesTeamMembersModalComponent, {
      data: {
        headerTitle: 'Select team members',
        templateAppearance: MaterialAppearance.FILL,
        autocompletePlaceholder: this.teamMembersText,
        inputsThemeColor: 'accent',
      },
      panelClass: ['medium-size-modal', 'team-members'],
    });
    this.subscription.add(
      matDialogRef.afterClosed().subscribe((visibleToText) => {
        if (visibleToText) {
          this.notesModalOptions.next(visibleToText);
        }
      }),
    );
    return matDialogRef;
  }

  private setupDataByResource(
    userId: number,
    resourceState: SharedResources,
  ): MojoShareResourceConfig {
    const data: MojoShareResourceConfig = {
      headerName: this.teamMembersText,
      autocompletePlaceholder: this.teamMembersText,
      accessLevelOptions: TEAM_ROLES.map((role) => ({
        id: role,
        displayName: role,
      })),
      enableCurrentUserRemove: false,
      resourceId: resourceState.resourceId,
      resourceType: resourceState.resourceType,
      currentUserId: userId,
      sortOrder: TEAM_ROLES,
      showAvatars: true,
      templateAppearance: MaterialAppearance.FILL,
      inputsThemeColor: 'accent',
      displayChipsBelow: true,
      showRoles: false,
    };
    switch (resourceState.resourceType) {
      case SharedResourcesType.talentPool: {
        return data;
      }
      case SharedResourcesType.job: {
        data.enableCurrentUserRemove = true;
        data.showRoles = true;
        return data;
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
